import { useState, useEffect } from 'react'
import { Box, Flex, VStack, Grid, Input } from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, Controller, useFormColors, FormSection, FormHeader, FormActions, FormTabs } from '@minetrace/ui/forms'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  LuUser as User,
  LuShieldCheck as ShieldCheck,
  LuCheck as Check,
  LuKeyRound as Key,
} from 'react-icons/lu'
import { Text, useT } from '@minetrace/ui'
import { statusColors } from '../../theme'
import { usuariosService } from '../../api/services'
import { useRequest } from '../../hooks'
import type { UpdateUsuarioRequest } from '../../api/models/request'
import type { UsuarioResponse, RoleResponse } from '../../api/models/response'
import CharCounter from '../../components/CharCounter'
import { PersonFieldLengths, UserFieldLengths, UiFieldLengths } from '../../lib/fieldLengths'
import { phoneRegister } from '../../lib/inputSanitizers'

type TabType = 'datos' | 'roles'

// Zod Schema (edit-only)
const editSchema = z.object({
  names: z.string().min(2, 'usuarios.nuevo.zod.minTwo'),
  paternalSurname: z.string().min(2, 'usuarios.nuevo.zod.minTwo'),
  maternalSurname: z.string().optional(),
  documentType: z.number(),
  identityDoc: z.string().min(1, 'usuarios.nuevo.zod.required'),
  email: z.string().email('usuarios.nuevo.zod.emailInvalid').optional().or(z.literal('')),
  phoneMobile: z.string().optional(),
  username: z.string().min(3, 'usuarios.nuevo.zod.minThree'),
  password: z.string().optional(),
  newPassword: z.string().min(6, 'usuarios.nuevo.zod.minSix').optional().or(z.literal('')),
  userStatus: z.number(),
  roleIds: z.array(z.string()).min(1, 'usuarios.nuevo.zod.rolesRequired'),
})

type FormData = z.infer<typeof editSchema>

export default function NuevoUsuarioPage() {
  const navigate = useNavigate()
  const c = useFormColors()
  const { t } = useT()
  const { id } = useParams()

  const [activeTab, setActiveTab] = useState<TabType>('datos')
  const [roles, setRoles] = useState<RoleResponse[]>([])
  const [loadingUser, setLoadingUser] = useState(true)
  const [, setExistingUser] = useState<UsuarioResponse | null>(null)

  const formMethods = useForm<FormData>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      names: '',
      paternalSurname: '',
      maternalSurname: '',
      documentType: 1,
      identityDoc: '',
      email: '',
      phoneMobile: '',
      username: '',
      password: '',
      userStatus: 2,
      roleIds: [],
    },
  })
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = formMethods

  const { execute: updateUser } = useRequest(
    (payload: { id: string; data: UpdateUsuarioRequest }) => usuariosService.update(payload.id, payload.data),
    { successMessage: t('usuarios.nuevo.toasts.updated') }
  )

  // Load roles
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const data = await usuariosService.getRoles()
        setRoles(data)
      } catch {
        // silently fail
      }
    }
    loadRoles()
  }, [])

  // Load existing user for edit (redirect if no id)
  useEffect(() => {
    if (!id) {
      navigate('/app/usuarios')
      return
    }
    const loadUser = async () => {
      setLoadingUser(true)
      try {
        const u = await usuariosService.getById(id)
        setExistingUser(u)
        reset({
          names: u.names,
          paternalSurname: u.paternalSurname,
          maternalSurname: u.maternalSurname ?? '',
          documentType: u.documentType,
          identityDoc: u.identityDoc,
          email: u.email ?? '',
          phoneMobile: u.phoneMobile ?? '',
          username: u.username,
          password: '',
          userStatus: u.userStatus,
          roleIds: u.roles.map((r) => r.roleId),
        })
      } catch {
        // silently fail
      } finally {
        setLoadingUser(false)
      }
    }
    loadUser()
  }, [id, reset, navigate])

  const selectedRoleIds = watch('roleIds')
  const documentType = watch('documentType')
  const userStatus = watch('userStatus')
  const isActiveStatus = userStatus === 2

  const handleToggleRole = (roleId: string) => {
    if (selectedRoleIds.includes(roleId)) {
      setValue('roleIds', selectedRoleIds.filter((rid) => rid !== roleId), { shouldValidate: true })
    } else {
      setValue('roleIds', [...selectedRoleIds, roleId], { shouldValidate: true })
    }
  }

  const onSubmit = async (data: FormData) => {
    if (!id) return

    const result = await updateUser({
      id,
      data: {
        names: data.names,
        paternalSurname: data.paternalSurname,
        maternalSurname: data.maternalSurname || undefined,
        documentType: data.documentType,
        identityDoc: data.identityDoc,
        email: data.email || undefined,
        phoneMobile: data.phoneMobile || undefined,
        userStatus: data.userStatus,
        newPassword: data.newPassword || undefined,
        roleIds: data.roleIds,
      },
    })
    if (!result) return
    setTimeout(() => navigate('/app/usuarios'), 1500)
  }

  const getRolColor = (code: string): string => {
    const colorMap: Record<string, string> = {
      superadmin: '#c8442f',
      admin: '#d49a2a',
      operador: '#3d6fa8',
      transportista: '#8B5CF6',
      proveedor: '#2a8a87',
    }
    return colorMap[code] ?? c.accent
  }

  const inputStyles = {
    bg: c.bgInput,
    border: 'none',
    borderRadius: 'xl',
    color: c.text,
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', ring: '1px', ringColor: c.accent },
  } as const

  const errorInputStyles = {
    ...inputStyles,
    ring: '1px',
    ringColor: c.error,
  } as const

  const selectStyles: React.CSSProperties = {
    width: '100%',
    padding: '0.625rem 0.75rem',
    borderRadius: '0.75rem',
    border: 'none',
    fontSize: '0.875rem',
    background: c.bgInput,
    color: c.text,
    outline: 'none',
  }

  const datosHasErrors = Boolean(
    errors.names || errors.paternalSurname || errors.identityDoc || errors.username || errors.password
  )
  const rolesHasErrors = Boolean(errors.roleIds)

  const tabs = [
    { key: 'datos' as const, label: t('usuarios.nuevo.tabs.datos'), hasError: datosHasErrors },
    { key: 'roles' as const, label: t('usuarios.nuevo.tabs.roles', { count: selectedRoleIds.length }), hasError: rolesHasErrors },
  ]

  if (loadingUser) {
    return (
      <VStack gap={6} alignItems="stretch">
        <FormHeader
          title={t('usuarios.nuevo.loadingTitle')}
          subtitle={t('usuarios.nuevo.loadingSubtitle')}
          backPath="/app/usuarios"
        />
        <Box p={8} textAlign="center">
          <Text tid="usuarios.nuevo.loadingData" color={c.textMuted} />
        </Box>
      </VStack>
    )
  }

  return (
    <VStack gap={6} alignItems="stretch">

      <FormHeader
        title={t('usuarios.nuevo.titleEdit')}
        subtitle={t('usuarios.nuevo.subtitleEdit')}
        backPath="/app/usuarios"
      />

      <FormTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Tab: Datos Básicos */}
        {activeTab === 'datos' && (
          <VStack gap={6} alignItems="stretch">
            <FormSection icon={User} titleTid="usuarios.nuevo.sections.infoPersonal">
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                {/* Nombres */}
                <Box>
                  <Text tid="usuarios.nuevo.fields.namesRequired" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
                  <Input
                    {...register('names')}
                    placeholder={t('usuarios.nuevo.fields.namesPlaceholder')}
                    maxLength={PersonFieldLengths.names}
                    {...(errors.names ? errorInputStyles : inputStyles)}
                  />
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    {errors.names ? (
                      <Text fontSize="xs" color={c.error}>{t(errors.names.message ?? '')}</Text>
                    ) : <span />}
                    <CharCounter name="names" max={PersonFieldLengths.names} mt={0} />
                  </Flex>
                </Box>

                {/* Apellido Paterno */}
                <Box>
                  <Text tid="usuarios.nuevo.fields.paternalSurnameRequired" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
                  <Input
                    {...register('paternalSurname')}
                    placeholder={t('usuarios.nuevo.fields.paternalSurnamePlaceholder')}
                    maxLength={PersonFieldLengths.paternalSurname}
                    {...(errors.paternalSurname ? errorInputStyles : inputStyles)}
                  />
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    {errors.paternalSurname ? (
                      <Text fontSize="xs" color={c.error}>{t(errors.paternalSurname.message ?? '')}</Text>
                    ) : <span />}
                    <CharCounter name="paternalSurname" max={PersonFieldLengths.paternalSurname} mt={0} />
                  </Flex>
                </Box>

                {/* Apellido Materno */}
                <Box>
                  <Text tid="usuarios.nuevo.fields.maternalSurname" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
                  <Input
                    {...register('maternalSurname')}
                    placeholder={t('usuarios.nuevo.fields.maternalSurnamePlaceholder')}
                    maxLength={PersonFieldLengths.maternalSurname}
                    {...inputStyles}
                  />
                  <CharCounter name="maternalSurname" max={PersonFieldLengths.maternalSurname} />
                </Box>

                {/* Tipo Documento + Número */}
                <Box>
                  <Text tid="usuarios.nuevo.fields.identityDocRequired" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
                  <Flex gap={2}>
                    <Controller
                      name="documentType"
                      control={control}
                      render={({ field }) => (
                        <select
                          value={field.value}
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                          style={{ ...selectStyles, width: '100px', flexShrink: 0 }}
                        >
                          <option value={1}>DNI</option>
                          <option value={2}>CE</option>
                        </select>
                      )}
                    />
                    <Input
                      {...register('identityDoc')}
                      placeholder={documentType === 1 ? t('usuarios.nuevo.fields.dniPlaceholder') : t('usuarios.nuevo.fields.cePlaceholder')}
                      maxLength={PersonFieldLengths.identityDoc}
                      fontFamily="monospace"
                      {...(errors.identityDoc ? errorInputStyles : inputStyles)}
                    />
                  </Flex>
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    {errors.identityDoc ? (
                      <Text fontSize="xs" color={c.error}>{t(errors.identityDoc.message ?? '')}</Text>
                    ) : <span />}
                    <CharCounter name="identityDoc" max={PersonFieldLengths.identityDoc} mt={0} />
                  </Flex>
                </Box>

                {/* Email */}
                <Box>
                  <Text tid="usuarios.nuevo.fields.email" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
                  <Input
                    type="email"
                    {...register('email')}
                    placeholder={t('usuarios.nuevo.fields.emailPlaceholder')}
                    maxLength={PersonFieldLengths.email}
                    {...(errors.email ? errorInputStyles : inputStyles)}
                  />
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    {errors.email ? (
                      <Text fontSize="xs" color={c.error}>{t(errors.email.message ?? '')}</Text>
                    ) : <span />}
                    <CharCounter name="email" max={PersonFieldLengths.email} mt={0} />
                  </Flex>
                </Box>

                {/* Teléfono */}
                <Box>
                  <Text tid="usuarios.nuevo.fields.phone" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
                  <Input
                    {...phoneRegister(register('phoneMobile'))}
                    placeholder={t('usuarios.nuevo.fields.phonePlaceholder')}
                    maxLength={PersonFieldLengths.phoneMobile}
                    {...inputStyles}
                  />
                  <CharCounter name="phoneMobile" max={PersonFieldLengths.phoneMobile} />
                </Box>

                {/* Estado */}
                {(
                  <Box gridColumn={{ md: 'span 2' }}>
                    <Text tid="usuarios.nuevo.fields.status" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
                    <Flex
                      as="button"
                      type="button"
                      alignItems="center"
                      gap={3}
                      px={4}
                      py={3}
                      borderRadius="xl"
                      bg={c.bgHover}
                      cursor="pointer"
                      onClick={() => setValue('userStatus', isActiveStatus ? 3 : 2)}
                      transition="all 0.2s"
                      _hover={{ bg: c.border }}
                      width="fit-content"
                    >
                      <Box
                        width="44px"
                        height="24px"
                        borderRadius="full"
                        bg={isActiveStatus ? statusColors.success : 'rgba(255, 255, 255, 0.1)'}
                        position="relative"
                        transition="background 0.2s"
                      >
                        <Box
                          position="absolute"
                          top="2px"
                          left={isActiveStatus ? '22px' : '2px'}
                          width="20px"
                          height="20px"
                          borderRadius="full"
                          bg="white"
                          transition="left 0.2s"
                          boxShadow="0 1px 3px rgba(0,0,0,0.3)"
                        />
                      </Box>
                      <Text fontSize="sm" color={isActiveStatus ? statusColors.success : c.textMuted} fontWeight="500">
                        {isActiveStatus ? t('usuarios.nuevo.fields.statusActive') : t('usuarios.nuevo.fields.statusInactive')}
                      </Text>
                    </Flex>
                  </Box>
                )}
              </Grid>
            </FormSection>

            {/* Credenciales */}
            <FormSection icon={Key} titleTid="usuarios.nuevo.sections.credenciales">
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                {/* Username */}
                <Box>
                  <Text tid="usuarios.nuevo.fields.usernameRequired" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
                  <Input
                    {...register('username')}
                    placeholder={t('usuarios.nuevo.fields.usernamePlaceholder')}
                    disabled
                    maxLength={UserFieldLengths.username}
                    {...(errors.username ? errorInputStyles : inputStyles)}
                    opacity={0.6}
                  />
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    <Text tid="usuarios.nuevo.fields.usernameHelp" fontSize="xs" color={c.textMuted} />
                    <CharCounter name="username" max={UserFieldLengths.username} mt={0} />
                  </Flex>
                  {errors.username && (
                    <Text fontSize="xs" color={c.error} mt={1}>{t(errors.username.message ?? '')}</Text>
                  )}
                </Box>

                {/* Password (create) / New Password (edit) */}
                <Box>
                  <Text tid="usuarios.nuevo.fields.newPassword" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
                  <Input
                    type="password"
                    {...register('newPassword')}
                    placeholder={t('usuarios.nuevo.fields.newPasswordPlaceholder')}
                    maxLength={UiFieldLengths.password}
                    {...inputStyles}
                  />
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    <Text tid="usuarios.nuevo.fields.newPasswordHelp" fontSize="xs" color={c.textMuted} />
                    <CharCounter name="newPassword" max={UiFieldLengths.password} mt={0} />
                  </Flex>
                </Box>
              </Grid>
            </FormSection>
          </VStack>
        )}

        {/* Tab: Roles */}
        {activeTab === 'roles' && (
          <VStack gap={6} alignItems="stretch">
            <FormSection icon={ShieldCheck} titleTid="usuarios.nuevo.sections.rolesDisponibles" descriptionTid="usuarios.nuevo.sections.rolesDescription">
              {errors.roleIds && (
                <Box
                  mb={4}
                  p={3}
                  bg={`${c.error}18`}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={`${c.error}4D`}
                >
                  <Text fontSize="sm" color={c.error}>{t(errors.roleIds.message ?? '')}</Text>
                </Box>
              )}

              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                {roles.map((rol) => {
                  const isSelected = selectedRoleIds.includes(rol.id)
                  const color = getRolColor(rol.code)

                  return (
                    <Box
                      key={rol.id}
                      as="button"
                      type="button"
                      p={4}
                      borderRadius="xl"
                      border="2px solid"
                      borderColor={isSelected ? color : c.border}
                      bg={isSelected ? `${color}10` : c.bgHover}
                      cursor="pointer"
                      textAlign="left"
                      transition="all 0.2s"
                      _hover={{ borderColor: color, bg: `${color}10` }}
                      onClick={() => handleToggleRole(rol.id)}
                      position="relative"
                    >
                      {isSelected && (
                        <Box
                          position="absolute" top={3} right={3}
                          width="24px" height="24px" borderRadius="full"
                          bg={color} display="flex" alignItems="center" justifyContent="center"
                        >
                          <Check size={14} color="white" />
                        </Box>
                      )}

                      <Box
                        display="inline-block" px={2} py={1} borderRadius="md"
                        fontSize="10px" fontWeight="bold" textTransform="uppercase"
                        letterSpacing="0.05em" bg={`${color}20`} color={color} mb={2}
                      >
                        {rol.code}
                      </Box>

                      <Text fontSize="md" fontWeight="bold" color={c.text} mb={1}>{rol.name}</Text>
                      {rol.description && (
                        <Text fontSize="xs" color={c.textMuted} lineClamp={2}>{rol.description}</Text>
                      )}
                    </Box>
                  )
                })}
              </Grid>
            </FormSection>
          </VStack>
        )}

        <FormActions
          submitLabel={t('usuarios.nuevo.actions.saveChanges')}
          cancelPath="/app/usuarios"
          isSubmitting={isSubmitting}
        />
      </form>
      </FormProvider>
    </VStack>
  )
}
